import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../framework/BaseWebComponentController';
import { AccountDetailsWebComponent } from '../component/AccountDetailsWebComponent';
import { templateAccountDetails } from '../view/template';
import { AccountDetailsWebView } from '../view/AccountDetailsWebView';
import { AccountDetails, AccountDetailsEvents, ADUser, ADUserSelectorType, LanguageData, TimezoneData } from '../types';
import { AccountActionContext } from '../../types/AccountActionContext';
import { ToggleEvents } from '../../../../ui/gzToggle/types';
import { AccountSource } from '../../types/AccountSources';
import { AccountDetailsDataProvider } from '../dataProvider/AccountDetailsDataProvider';
import { showToast } from '../../../helpers/showToast';
import { ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { translateFn } from '../../../../utils/translator.helper';

export class AccountDetailsWebController extends BaseWebComponentController {
  public component: AccountDetailsWebComponent;
  public view: AccountDetailsWebView;
  public viewConstructor: typeof AccountDetailsWebView;
  public currentCompanyId: string | number;

  constructor(component: AccountDetailsWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: AccountDetailsWebView,
      viewTemplate: templateAccountDetails,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$activeDirectorySynchronizeButton,
        event: 'click',
        callback: () => {
          this.view.openSyncWarning();
        },
      },
      {
        element: this.view.dom.$importFromActiveDirectoryToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.changeCreateAccountSource,
      },
      {
        element: this.view.dom.$usernameADFormFieldSelector,
        event: 'selectionChange',
        callback: this.onSelectADUser,
      },
      {
        element: this.view.dom.$passwordFormField,
        event: 'change',
        callback: this.onPasswordChange,
      },
      {
        element: this.view.dom.$companyFormField.Input,
        event: 'selectionChange',
        callback: this.onCompanyChanged,
      },
      {
        element: this.view.dom.$activeDirectorySynchronizeButton,
        event: AccountDetailsEvents.RESYNC_AD_IN_PROGRESS,
        callback: this.onADSyncInProgress,
      },
    ];
  }

  public async getDataForView(): Promise<AccountDetails> {
    let viewData = this.component.getViewData() ?? {};
    viewData.timezoneOptions = await this.getTimezones();
    viewData.languageOptions = await this.getLanguages();
    return viewData;
  }

  private async getTimezones(): Promise<TimezoneData[]> {
    try {
      const accountDetailsDataProvider: AccountDetailsDataProvider = new AccountDetailsDataProvider();
      const result = await accountDetailsDataProvider.getUsersTimezone();
      return result.data as TimezoneData[];
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  private async getLanguages(): Promise<LanguageData[]> {
    return (await this.dependencies.api.clients.Languages.getLanguages().toPromise()) as LanguageData[];
  }

  private changeCreateAccountSource(event: WebComponentEvent): void {
    const source = event.detail.checked ? AccountSource.ACTIVEDIR : AccountSource.MANUAL;
    this.view.changeFormFieldsVisibilityDependingOnSourceAccount(source);
    this.view.resetFormFieldsOnSourceAccountChange(source);

    this.component.dispatchEvent(new CustomEvent(AccountDetailsEvents.CHANGE_ACCOUNT_SOURCE, event));
  }

  private onSelectADUser(event: WebComponentEvent): void {
    const selectedValue = event.detail.selectedValue;
    if (!selectedValue) {
      return;
    }

    // fill user details on select AD user
    const adUser: ADUser = {} as ADUser;
    for (const user of this.view.dom.$usernameADFormFieldSelector.Input.filterOptions) {
      if (user.value === selectedValue) {
        const adUserFromSelector: any = user as ADUserSelectorType;
        adUser.userName = adUserFromSelector.label;
        adUser._id = adUserFromSelector.value as string;
        adUser.email = adUserFromSelector.email;
        adUser.fullName = adUserFromSelector.fullName;
        break;
      }
    }

    this.view.fillUserDetails(adUser);
  }

  private onCompanyChanged(event: WebComponentEvent): void {
    if (!event.detail.selectedValue) {
      return;
    }

    if (this.currentCompanyId !== this.view.dom.$companyFormField.value) {
      this.component.dispatchEvent(new CustomEvent(AccountDetailsEvents.COMPANY_SELECTION_CHANGED, event));
      this.currentCompanyId = this.view.dom.$companyFormField.value;
    }
  }

  private onPasswordChange(event: WebComponentEvent): void {
    this.view.dom.$confirmPasswordFormField.validate();
  }

  private onADSyncInProgress(event: WebComponentEvent) {
    this.component.dispatchEvent(new CustomEvent(AccountDetailsEvents.RESYNC_AD_IN_PROGRESS, event));

    const accountDetailsDataProvider: AccountDetailsDataProvider = new AccountDetailsDataProvider();
    accountDetailsDataProvider
      .resyncLdapUsers()
      .then(() => {
        setTimeout(() => {
          this.checkForFinishedADResync().then().catch();
        }, 3000);
      })
      .catch(error => {
        console.log(error);
      });
  }

  /**
   * Check multiple times if AD sync has been finalized
   */
  private async checkForFinishedADResync(): Promise<void> {
    let finishedResync = false;
    let tryNo = 5;
    do {
      try {
        const accountDetailsDataProvider: AccountDetailsDataProvider = new AccountDetailsDataProvider();
        finishedResync = await accountDetailsDataProvider.finishedResyncLdapUsers();
      } catch (error) {
        console.log(error);
      }

      tryNo--;
      if (tryNo === 0) {
        break;
      }
    } while (!finishedResync);

    await this.view.fillADUserDropdown();
    this.view.resetFormFieldsOnSourceAccountChange(AccountSource.ACTIVEDIR);
    this.view.discardFormGroupChangesOnADUser();
    this.view.createFormGroup();

    this.component.dispatchEvent(
      new CustomEvent(AccountDetailsEvents.RESYNC_AD_DONE, {
        bubbles: true,
        composed: true,
      })
    );

    if (tryNo === 0 && !finishedResync) {
      showToast(ToastSeverity.ERROR, translateFn('Synchronization failed.'));
      this.view.dom.$activeDirectorySynchronizeButton.disabled = false;
      return;
    }

    showToast(ToastSeverity.SUCCESS, translateFn('Accounts synchronized.'));
    this.view.dom.$activeDirectorySynchronizeButton.disabled = false;
  }
}
